import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAdminAward,
  IAwardType,
  IGetAdminAwardsResponse,
  IGetAwardTypesResponse,
} from '../../../interface/classveew-admin/admin-award.interface';
import {
  fetchAdminAwards,
  fetchAwardTypes,
  createAward,
  editAward,
  deleteAward,
  toggleAward,
} from '../../action/classveew-admin/admin-award.action';
import { tr } from '../../../i18n/i18n';

interface IAdminAwardState {
  awards: IAdminAward[];
  awardTypes: IAwardType[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  isLoading: boolean;
  isMutating: boolean;
  error: string | null;
}

const initialState: IAdminAwardState = {
  awards: [],
  awardTypes: [],
  page: 1,
  pageSize: 20,
  total: 0,
  totalPages: 0,
  isLoading: false,
  isMutating: false,
  error: null,
};

const adminAwardSlice = createSlice({
  name: 'adminAward',
  initialState,
  reducers: {
    clearAdminAwards: (state) => {
      state.awards = [];
      state.page = 1;
      state.total = 0;
      state.totalPages = 0;
    },
    clearAwardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch awards
      .addCase(fetchAdminAwards.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminAwards.fulfilled,
        (state, action: PayloadAction<IGetAdminAwardsResponse>) => {
          state.isLoading = false;
          state.awards = action.payload.data?.awards || [];
          state.page = action.payload.data?.page || 1;
          state.pageSize = action.payload.data?.pageSize || 20;
          state.total = action.payload.data?.total || 0;
          state.totalPages = action.payload.data?.totalPages || 0;
          state.error = null;
        },
      )
      .addCase(fetchAdminAwards.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || tr('admin_award.error_fetch_awards', 'Failed to fetch awards');
        state.awards = [];
      })
      // Fetch award types
      .addCase(fetchAwardTypes.pending, (state) => {
        state.error = null;
      })
      .addCase(
        fetchAwardTypes.fulfilled,
        (state, action: PayloadAction<IGetAwardTypesResponse>) => {
          state.awardTypes = action.payload.data || [];
        },
      )
      .addCase(fetchAwardTypes.rejected, (state, action) => {
        state.error =
          action.payload?.message ||
          tr('admin_award.error_fetch_award_types', 'Failed to fetch award types');
      })
      // Create award
      .addCase(createAward.pending, (state) => {
        state.isMutating = true;
        state.error = null;
      })
      .addCase(createAward.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(createAward.rejected, (state, action) => {
        state.isMutating = false;
        state.error =
          action.payload?.message || tr('admin_award.error_create_award', 'Failed to create award');
      })
      // Edit award
      .addCase(editAward.pending, (state) => {
        state.isMutating = true;
        state.error = null;
      })
      .addCase(editAward.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(editAward.rejected, (state, action) => {
        state.isMutating = false;
        state.error =
          action.payload?.message || tr('admin_award.error_update_award', 'Failed to update award');
      })
      // Delete award
      .addCase(deleteAward.pending, (state) => {
        state.isMutating = true;
        state.error = null;
      })
      .addCase(deleteAward.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(deleteAward.rejected, (state, action) => {
        state.isMutating = false;
        state.error =
          action.payload?.message || tr('admin_award.error_delete_award', 'Failed to delete award');
      })
      // Toggle award
      .addCase(toggleAward.pending, (state) => {
        state.isMutating = true;
        state.error = null;
      })
      .addCase(toggleAward.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(toggleAward.rejected, (state, action) => {
        state.isMutating = false;
        state.error =
          action.payload?.message ||
          tr('admin_award.error_toggle_award', 'Failed to toggle award status');
      });
  },
});

export const { clearAdminAwards, clearAwardError } = adminAwardSlice.actions;
export default adminAwardSlice.reducer;
